/// <reference  types="@types/googlemaps"  />

import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Router } from '@angular/router';
import {
  buildAddress,
  buildVoidGate,
} from '../../../../../data/factory/gate.factory';
import { Gate } from '../../../../../data/models/interfaces/arrival/gate.interface';
import { GateService } from '../../../../../data/services/arrival/gate.service';
import { UserUtil } from '../../../../../data/utils/user.utils';
import { CustomSnackbarService } from '../../../../../ui/components/custom-snackbar/custom-snackbar-service';
import { FlatNode } from '../../../../../ui/components/select-hierarquical-list/model/flat.node.interface';
import { SimpleNode } from '../../../../../ui/components/select-hierarquical-list/model/simple.node.interface';
import { ImageItem } from '../../../../../ui/components/select-simple-list/model/item-image.interface';

@Component({
  selector: 'app-gate-form',
  templateUrl: './gate-form.component.html',
  styleUrls: ['./gate-form.component.scss'],
})
export class GateFormComponent implements OnInit {
  @Output() closeModalEvent = new EventEmitter<boolean>();

  @ViewChild('addressText') addressText!: ElementRef;
  @ViewChild('gmap') gmapElement: any;

  private get schools() {
    return this.userUtil.getSchools();
  }

  place: google.maps.places.PlaceResult | undefined;
  listener!: google.maps.MapsEventListener;

  gateForm: FormGroup = this.fb.group({
    name: ['', [Validators.required]],
    address: ['', [Validators.required]],
    responsables: ['', [Validators.required]],
    classrooms: ['', [Validators.required]],
  });

  saveButtonDisable = false;
  title = 'Criar portaria';
  btnSaveText = 'Criar';
  responsableSelectAllText = 'Todos os funcionários';
  responsablePlaceholder = 'Pesquise pelo nome';
  responsables: ImageItem[] = [];

  segmentTreeData: SimpleNode[] = this.data.segmentTree;
  selectedIdsClassrooms: string[] = this.data.gate
    ? this.data.gate.idsClassrooms
    : [];
  selectedIdsResponsables: string[] = this.data.gate
    ? this.data.gate.idsResponsables
    : [];

  constructor(
    private userUtil: UserUtil,
    private gateService: GateService,
    private fb: FormBuilder,
    private snackbar: CustomSnackbarService,
    private router: Router,
    private dialogRef: MatDialogRef<GateFormComponent>,
    @Inject(MAT_DIALOG_DATA)
    private data: {
      gate: Gate;
      segmentTree: SimpleNode[];
      responsableList: ImageItem[];
    }
  ) {}

  async ngOnInit(): Promise<void> {
    this.updateResponsableList();
    if (this.data.gate) {
      this.populateForm();
      this.title = 'Editar portaria';
      this.btnSaveText = 'Salvar';
    } else {
      this.data.gate = buildVoidGate();
    }
  }

  private updateResponsableList(): void {
    this.data.responsableList.forEach((user) => {
      const item = {
        id: user.id,
        imageUrl: user.imageUrl,
        name: user.name,
        isChecked: this.isResponsableChecked(user.id!),
      };
      this.responsables.push(item);
    });
  }

  openList(evt: any, trigger: MatAutocompleteTrigger): void {
    evt.stopPropagation();
    if (trigger.panelOpen) {
      trigger.closePanel();
    } else {
      trigger.openPanel();
    }
  }

  placesToAutocomplete() {
    const options: google.maps.places.AutocompleteOptions = {
      componentRestrictions: { country: 'BR' },
      types: ['establishment', 'geocode'],
    };
    if (this.gateForm.controls['address'].value.length >= 4) {
      const autocomplete = new google.maps.places.Autocomplete(
        this.addressText.nativeElement,
        options
      );
      this.listener = google.maps.event.addListener(
        autocomplete,
        'place_changed',
        () => {
          this.place = autocomplete.getPlace();
        }
      );
    } else {
      google.maps.event.removeListener(this.listener);
      google.maps.event.clearInstanceListeners(this.addressText.nativeElement);
      this.removeGooglePlacesAutocompleteBox();
    }
  }

  removeGooglePlacesAutocompleteBox() {
    const autocompleteBox = document.getElementsByClassName('pac-container');
    while (autocompleteBox.length > 0) {
      autocompleteBox[0].parentNode?.removeChild(autocompleteBox[0]);
    }
  }

  private populateForm(): void {
    this.gateForm.controls['name'].setValue(this.data.gate.name);
    this.gateForm.controls['address'].setValue(this.data.gate.address.address);
    this.gateForm.controls['responsables'].setValue(
      this.getResponsablesNames()
    );

    let classrooms = '';
    this.segmentTreeData.forEach((segment) => {
      segment.children?.forEach((serie) => {
        serie.children?.forEach((classroom) => {
          if (this.data.gate.idsClassrooms.includes(classroom.id!)) {
            if (classrooms === '') classrooms += classroom.name;
            else classrooms += ',' + classroom.name;
          }
        });
      });
    });
    this.gateForm.controls['classrooms'].setValue(classrooms);
  }

  private isResponsableChecked(userId: string): boolean {
    if (this.data.gate) {
      return this.data.gate.idsResponsables.includes(userId);
    } else {
      return false;
    }
  }

  handleResponsableSelect(evt: ImageItem[]): void {
    const selectedResponsables = evt.filter((item) => item.isChecked);
    const names = selectedResponsables.map((item) => item.name);
    this.gateForm.controls['responsables'].setValue(names);

    const ids = selectedResponsables.map((evt) => evt.id);
    this.selectedIdsResponsables = ids as string[];
    this.gateForm.markAsDirty();
  }

  private getResponsablesNames(): string {
    let names = '';
    this.responsables.forEach(async (responsable) => {
      if (responsable.isChecked) {
        if (names == '') {
          names = responsable.name.trim();
        } else {
          names = names + ', ' + responsable.name.trim();
        }
      }
    });
    return names;
  }

  private editedGate(): Gate {
    return {
      id: '',
      name: this.gateForm.value.name,
      address: !this.place ? this.data.gate.address : buildAddress(this.place),
      idsResponsables: this.selectedIdsResponsables,
      idsClassrooms: this.selectedIdsClassrooms,
      idSchool: this.schools[0].id,
    };
  }

  saveGate() {
    if (this.gateForm.controls['address'].dirty && !this.place) {
      this.gateForm.controls['address'].setErrors({ incorrect: true });
      return;
    }
    this.saveButtonDisable = true;
    let gate = this.editedGate();
    if (this.data.gate.id != '') {
      gate.id = this.data.gate.id;
      this.gateService
        .update(gate)
        .toPromise()
        .then(() => {
          this.snackbar
            .showNotification('As alterações foram salvas', 2000)
            .afterDismissed()
            .toPromise()
            .then(() => {
              this.router
                .navigate(['gate-list']);
            });
          this.closeDialog();
        })
        .catch((err) => {
          this.saveButtonDisable = false;
          let errMsg = err.error.message
            ? err.error.message
            : 'Erro ao executar operação';
          this.snackbar.showNotification(errMsg, 2000);
        });
    } else {
      this.gateService
        .create(gate)
        .toPromise()
        .then(() => {
          this.snackbar
            .showNotification('A portaria foi criada', 2000)
            .afterDismissed()
            .toPromise()
            .then(() => {
              this.router
                .navigate(['gate-list']);
            });
          this.closeDialog();
        })
        .catch((err) => {
          this.saveButtonDisable = false;
          let errMsg = err.error.message
            ? err.error.message
            : 'Erro ao executar operação';
          this.snackbar.showNotification(errMsg, 2000);
        });
    }
  }

  closeDialog() {
    this.dialogRef.close();
  }

  handleSelect(evt: FlatNode[]) {
    const names = evt.map((evt) => evt.name);
    this.gateForm.controls['classrooms'].setValue(names);

    const ids = evt.map((evt) => evt.id);
    this.selectedIdsClassrooms = ids;
    this.gateForm.markAsDirty();
  }
}
